<script setup>
import { requestConclusionResult } from 'dashboard/composables/useConclusionResultPrompt';
import {
  defineProps,
  computed,
  inject,
  onMounted,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES, ATTACHMENT_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import {
  getPointerViewportPosition,
  getVisualViewport,
  layoutToVisual,
  layoutPx,
  clampToVisualViewport,
} from 'dashboard/helper/dashboardZoom';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { isGifMessage } from 'dashboard/helper/gifMediaHelper';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import wootConstants from 'dashboard/constants/globals';
import MessageApi from 'dashboard/api/inbox/message.js';
import { groupParticipantKey } from './groupParticipant.js';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);
const messageListElement = ref(null);

defineExpose({ messageListElement });

// Aceleres: o template tem duas raízes (ul + Teleport do menu de contexto),
// então o Vue não repassa os attrs automaticamente — sem o v-bind="$attrs"
// no ul, as classes do MessagesView (conversation-panel, overflow-y-auto…)
// eram descartadas e a lista crescia sem scroll, escondendo o composer.
defineOptions({ inheritAttrs: false });

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { isSelectionMode, enterSelectionMode } = useMessageSelection();
const markAsUnreadAction = inject('markAsUnread', null);
const markAsReadAction = inject('markAsRead', null);
const togglePinAction = inject('togglePin', null);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const teams = useMapGetter('teams/getTeams');
const historyPreviousLabel = 'Histórico anterior';
const conversationContextMenu = ref({
  isOpen: false,
  // Aceleres: `left`/`top` sao strings CSS ja em espaco de layout (com 'px'),
  // nao numeros — a conversao acontece em openConversationContextMenu.
  left: '0px',
  top: '0px',
});
const conversationContextMenuRef = ref(null);
const conversationContextMenuLabels = {
  selectMessages: 'Selecionar mensagens',
  markAsUnread: 'Marcar como não lida',
  markAsRead: 'Marcar como lida',
  pinConversation: 'Fixar conversa',
  unpinConversation: 'Desafixar conversa',
  closeConversation: 'Fechar conversa',
};
const isConversationResolved = computed(
  () => currentChat.value?.status === wootConstants.STATUS_TYPE.RESOLVED
);
const hasUnreadMessages = computed(
  () => Number(currentChat.value?.unread_count || 0) > 0
);
const isConversationPinned = computed(() => !!currentChat.value?.is_pinned);
const isPassiveSupervisorView = computed(() => {
  const conversation = currentChat.value;
  const assigneeId =
    conversation?.meta?.assignee?.id ?? conversation?.assignee_id;
  if (!assigneeId || Number(assigneeId) === Number(props.currentUserId)) {
    return false;
  }
  if (currentUser.value?.role === 'administrator') return true;

  const teamId =
    conversation?.team_id ??
    conversation?.team?.id ??
    conversation?.meta?.team?.id ??
    conversation?.inbox?.team_id;
  return (teams.value || []).some(
    team => Number(team.id) === Number(teamId) && Boolean(team.is_supervisor)
  );
});

const closeConversationContextMenu = () => {
  conversationContextMenu.value.isOpen = false;
};

const closeOpenMessageMenus = () => {
  emitter.emit(BUS_EVENTS.MESSAGE_INTERACTION_OPENED, {
    messageId: null,
    interaction: null,
  });
};

const handleMessageInteractionOpened = ({ interaction } = {}) => {
  if (interaction) closeConversationContextMenu();
};

function onConversationContextDocumentClick(event) {
  if (conversationContextMenuRef.value?.contains(event.target)) return;
  closeConversationContextMenu();
}

function onConversationContextKeydown(event) {
  if (event.key === 'Escape') {
    closeConversationContextMenu();
  }
}

const removeConversationContextMenuListeners = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener(
    'click',
    onConversationContextDocumentClick,
    true
  );
  document.removeEventListener('keydown', onConversationContextKeydown, true);
  document.removeEventListener('scroll', closeConversationContextMenu, true);
};

const addConversationContextMenuListeners = () => {
  if (typeof document === 'undefined') return;
  removeConversationContextMenuListeners();
  document.addEventListener('click', onConversationContextDocumentClick, true);
  document.addEventListener('keydown', onConversationContextKeydown, true);
  document.addEventListener('scroll', closeConversationContextMenu, true);
};

const openConversationContextMenu = event => {
  // So o corpo visual da bolha usa o menu da mensagem. A linha ocupa toda a
  // largura do chat e tambem tem um handler; nas laterais, abra o menu geral.
  if (event.target?.closest?.('.aceleres-bubble-surface')) {
    closeConversationContextMenu();
    if (!isSelectionMode.value) return;
  }

  // En móvil, la pulsación prolongada pertenece al mensaje: debe entrar al
  // modo de selección estilo WhatsApp, no abrir acciones de la conversación.
  // También evitamos que un segundo long-press durante la selección reabra
  // este menú por encima de la barra superior.
  if (
    isSelectionMode.value ||
    window.matchMedia?.('(max-width: 767px)').matches
  ) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    return;
  }
  if (getSelection().toString()) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
  closeOpenMessageMenus();

  // Aceleres 2026-08-25: os 240x92 sao a medida DECLARADA em CSS
  // (.acl-conversation-list-context-menu). Com `html { zoom: .8 }` o menu ocupa
  // 192x74 no ecra, que e a escala em que `clientX` e `innerWidth` falam — por
  // isso passam por `layoutToVisual` antes de entrar na conta. O ponto final
  // volta a layout em `layoutPx`, ja que o menu e `position: fixed` dentro do
  // `html` com zoom. Ver helper/dashboardZoom.js.
  const padding = 8;
  const viewport = getVisualViewport();
  const menuWidth = Math.min(
    layoutToVisual(240, viewport.zoom),
    viewport.width - padding * 2
  );
  const menuHeight = layoutToVisual(
    isPassiveSupervisorView.value ? 136 : 176,
    viewport.zoom
  );
  const pointer = getPointerViewportPosition(event);
  const { left, top } = clampToVisualViewport(
    pointer.x,
    pointer.y,
    menuWidth,
    menuHeight,
    padding,
    viewport
  );

  conversationContextMenu.value = {
    isOpen: true,
    left: layoutPx(left, viewport.zoom),
    top: layoutPx(top, viewport.zoom),
  };
  addConversationContextMenuListeners();
};

const startMessageSelection = () => {
  closeConversationContextMenu();
  enterSelectionMode();
};

const closeConversation = async () => {
  if (!currentChat.value?.id || isConversationResolved.value) return;

  closeConversationContextMenu();
  const conversationId = currentChat.value.id;
  // Aceleres: o vendedor informa o tipo de conclusão; cancelar não fecha.
  const conclusionResult = await requestConclusionResult();
  if (!conclusionResult) return;
  try {
    await store.dispatch('toggleStatus', {
      conversationId,
      status: wootConstants.STATUS_TYPE.RESOLVED,
      conclusionResult,
    });
    await store.dispatch('clearSelectedState');
    await router.push(accountScopedRoute('home'));
    useAlert('Conversa fechada');
  } catch {
    useAlert('Falha ao fechar a conversa');
  }
};

const updateConversationReadState = async () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) return;

  closeConversationContextMenu();
  try {
    if (hasUnreadMessages.value) {
      if (typeof markAsReadAction === 'function') {
        await markAsReadAction(conversationId);
      } else {
        await store.dispatch('markMessagesRead', {
          id: conversationId,
          conversation: currentChat.value,
        });
      }
      return;
    }

    if (typeof markAsUnreadAction === 'function') {
      await markAsUnreadAction(conversationId);
    } else {
      await store.dispatch('markMessagesUnread', { id: conversationId });
      await store.dispatch('clearSelectedState');
      await router.push(accountScopedRoute('home'));
    }
  } catch {
    useAlert('Falha ao atualizar a leitura da conversa');
  }
};

const toggleConversationPin = async () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) return;

  closeConversationContextMenu();
  try {
    if (typeof togglePinAction === 'function') {
      await togglePinAction(conversationId);
      return;
    }

    const result = await store.dispatch('togglePin', conversationId);
    useAlert(
      result?.is_pinned ? 'Conversa fixada no topo.' : 'Conversa desafixada.'
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível alterar a fixação da conversa.'
    );
  }
};

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};

const isSameDay = (ts1, ts2) => {
  const d1 = new Date(ts1 * 1000);
  const d2 = new Date(ts2 * 1000);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const getDayLabel = ts => {
  const d = new Date(ts * 1000);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const getHistoryMetadata = item => {
  return item?.firstMessage?.historyMetadata || null;
};

const shouldShowHistoryContext = (item, previousItem) => {
  const currentHistory = getHistoryMetadata(item);
  if (!currentHistory?.segmentKey) {
    return false;
  }

  const previousHistory = getHistoryMetadata(previousItem);
  return currentHistory.segmentKey !== previousHistory?.segmentKey;
};

const formatHistoryContext = historyMetadata => {
  if (!historyMetadata) {
    return '';
  }

  const parts = [];
  if (historyMetadata.teamName) {
    parts.push(`Equipe ${historyMetadata.teamName}`);
  }
  if (historyMetadata.assigneeName) {
    parts.push(`Vendedor ${historyMetadata.assigneeName}`);
  }

  return parts.join(' • ');
};

// ============================================================================
// Aceleres: agrupamento de imagens/videos tipo WhatsApp
// ============================================================================
// Quando 3 ou mais mensagens consecutivas do mesmo remetente forem imagens
// e/ou videos sem legenda (em qualquer combinacao, ex: 2 imagens + 1 video),
// independentemente do intervalo entre os envios, e a última estiver
// estabilizada há pelo menos 15 segundos, elas serão exibidas em um único
// álbum em grade. A interface continua mostrando primeiro as imagens/videos
// separados e agrupando-os depois que o lote termina.
// Toda a decisao acontece aqui (no MessageList) e passa `albumMessages`
// como prop pra <Message>, que roteia pra ImageAlbumBubble.

// Aceleres 2026-08-18 -- passou de 4 para 3, a pedido.
//
// So o numero muda. A grade de ImageAlbum.vue ja sabia desenhar tres — tem um
// caso proprio (`total === 3`: o primeiro azulejo ocupa as duas colunas com
// proporcao 2/1 e os outros dois ficam quadrados por baixo), que nunca era
// alcancado porque o corte estava em 4. E o resumo "+N" nao aparece: ele conta
// `total - (PREVIEW_TILE_COUNT + 1)`, que com tres da zero.
//
// Figurinha e GIF continuam de fora, como pedido — a exclusao ja existia em
// `isAlbumEligibleMessage` (`isWameStickerMessage` e `isGifMessage`), nao foi
// preciso acrescentar nada. Figurinha tem o seu proprio album, de pares; o GIF
// nunca entra em album nenhum, porque numa grade perderia a animacao e o botao
// de repetir.
const MIN_ALBUM_SIZE = 3;
const MIN_STICKER_ALBUM_SIZE = 2;

// Aceleres 2026-08-17: o álbum fecha ao ABRIR a conversa, não com o tempo.
//
// Antes um run virava álbum 15s depois de a última mensagem assentar, e o
// agrupamento acontecia à frente do agente: as bolhas que ele estava a ver
// colapsavam sozinhas numa grade. Agora só agrupa o que já lá estava quando a
// conversa foi aberta; o que chega em tempo real fica como bolha solta até o
// agente trocar de conversa ou recarregar a página.
//
// A marca é o MAIOR id de mensagem presente no momento em que a conversa
// abriu. Id serve melhor que horário: é monotónico e não sofre do desvio de
// relógio entre servidor e navegador que obrigava à espera dupla de antes. E
// resolve a paginação de graça — mensagem antiga trazida depois pela rolagem
// tem id MENOR que a marca, logo continua a agrupar.
const albumBaselineMessageId = ref(0);
let albumBaselineConversationId = null;

watch(
  [() => currentChat.value?.id, allMessages],
  ([conversationId, messages]) => {
    if (conversationId !== albumBaselineConversationId) {
      albumBaselineConversationId = conversationId;
      albumBaselineMessageId.value = 0;
    }
    // Só na primeira leva com mensagens: daí em diante a marca fica parada, e
    // é isso que mantém fora do álbum tudo o que chegar depois.
    if (albumBaselineMessageId.value === 0 && messages.length) {
      albumBaselineMessageId.value = Math.max(
        ...messages.map(message => Number(message.id) || 0)
      );
    }
  },
  { immediate: true }
);

// A API normalmente envia createdAt em segundos Unix, mas mensagens
// recebidas em tempo real podem chegar como string, ISO ou milissegundos.
// Normalizar aqui evita que uma unidade diferente impeça o álbum de fechar.
const toEpochSeconds = value => {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return numericValue > 1e12 ? Math.floor(numericValue / 1000) : numericValue;
  }

  const parsedValue =
    typeof value === 'string' ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsedValue) ? Math.floor(parsedValue / 1000) : 0;
};

const messageCreatedAt = message => toEpochSeconds(message?.createdAt);

const normalizeText = value =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const TECHNICAL_MEDIA_LABELS = new Set([
  'mensagem recebida',
  '[mensagem recebida]',
  'message received',
  '[message received]',
  'mídia recebida',
  '[mídia recebida]',
  'midia recebida',
  '[midia recebida]',
  'media received',
  '[media received]',
]);

const isCaptionlessMediaMessage = message => {
  const content = normalizeText(message?.content);
  return !content || TECHNICAL_MEDIA_LABELS.has(content);
};

// Aceleres: checks comuns aos dois tipos de álbum (foto/vídeo em grade e
// stickers em par) — status, privacidade, tipo e reply. O que muda entre
// os dois é só o filtro de anexo logo abaixo.
const isGroupableMediaMessage = msg => {
  if (!msg) return false;
  if (msg.status === 'failed') return false;
  if (msg.private) return false;
  if (
    msg.messageType !== MESSAGE_TYPES.INCOMING &&
    msg.messageType !== MESSAGE_TYPES.OUTGOING
  )
    return false;
  if (!Array.isArray(msg.attachments) || msg.attachments.length !== 1)
    return false;
  const ca = msg.contentAttributes || {};
  if (ca.inReplyTo || ca.in_reply_to) return false;
  return true;
};

const isAlbumEligibleMessage = msg => {
  if (!isGroupableMediaMessage(msg)) return false;
  const attachment = msg.attachments[0];
  const attachmentType = String(
    attachment.fileType ||
      attachment.file_type ||
      attachment.contentType ||
      attachment.content_type ||
      ''
  ).toLowerCase();
  const isImageAttachment =
    attachmentType === ATTACHMENT_TYPES.IMAGE ||
    attachmentType.startsWith('image/');
  const isVideoAttachment =
    attachmentType === ATTACHMENT_TYPES.VIDEO ||
    attachmentType.startsWith('video/');
  if (!isImageAttachment && !isVideoAttachment) return false;
  // Stickers chegam como anexo image/webp, então batem em isImageAttachment
  // acima, mas não são fotos — vão pro álbum de stickers (2 colunas) abaixo,
  // não pro álbum de fotos.
  if (isWameStickerMessage(msg)) return false;
  // Aceleres 2026-08-12: GIF também não é foto nem vídeo, e não entra em álbum
  // nenhum — cada GIF fica na sua bolha. Pelo anexo ele é indistinguível: o GIF
  // do WhatsApp é um MP4 mudo (`file_type: video`) e um .gif de verdade é
  // `image/gif`, então os dois batiam nos testes acima e 4 GIFs seguidos viravam
  // uma grade só. Um GIF numa grade também perderia o que o torna um GIF: a
  // animação a correr e o botão de repetir.
  if (isGifMessage(msg)) return false;
  if (!isCaptionlessMediaMessage(msg)) return false;
  return true;
};

// Aceleres: identidade de AUTOR para fins de agrupamento.
//
// Numa conversa de grupo o Chatwoot guarda um contato só — o grupo —, então
// todas as mensagens recebidas trazem o mesmo `senderId` e o painel as tratava
// como uma pessoa: falas de participantes diferentes caíam no mesmo bloco
// agrupado (e no mesmo álbum de mídia). Quem realmente enviou vive em
// `content_attributes`; ao compor essa chave aqui, os quatro consumidores
// abaixo — agrupamento de bolhas, álbum de fotos, álbum de stickers e dedup —
// passam a separar por participante de uma vez só.
//
// Conversa individual não tem participante: a chave cai no `senderId` puro e o
// comportamento permanece exatamente o de antes.
// Aceleres: ORIGEM da autoria, segunda metade da identidade de agrupamento.
//
// Uma mensagem sincronizada da WAME sem autor resolvido é rotulada com o nome
// do canal (isUnattributedWameMessage, em useDisplaySenderName.js); a enviada
// pelo painel é rotulada com o nome de quem enviou. As duas carregam o MESMO
// `senderId` quando quem envia é o dono do token admin da empresa — e aí o run
// juntava as duas num álbum só, cujo rótulo sai da mensagem-cabeça. Foi assim
// que uma figurinha enviada pelo painel apareceu com o nome do canal, por
// causa de um vídeo mandado do celular logo em seguida.
const unattributedWameMarkOf = msg => {
  const attrs = msg?.contentAttributes || {};
  const isWameSync = !!(attrs.wameSync ?? attrs.wame_sync);
  const hasResolvedAuthor = !!(
    attrs.wameAuthorChatwootUserId ?? attrs.wame_author_chatwoot_user_id
  );

  return isWameSync && !hasResolvedAuthor ? ':wame-unattributed' : '';
};

const senderIdOf = msg => {
  const baseId = msg?.senderId ?? msg?.sender?.id ?? null;
  const participant = groupParticipantKey(msg?.contentAttributes);
  const base = participant ? `${baseId ?? 'group'}:${participant}` : baseId;

  // Sem a marca, devolve exatamente o que devolvia antes (inclusive `null`):
  // interpolar sempre transformaria `null` em "null" e mudaria a chave de
  // dedup, que usa `senderIdOf(msg) || ''`.
  const mark = unattributedWameMarkOf(msg);

  return mark ? `${base ?? 'none'}${mark}` : base;
};

const shouldGroupWithNextMessage = (current, next) => {
  if (!next) return false;
  if (next.status === 'failed') return false;
  if (senderIdOf(next) !== senderIdOf(current)) return false;
  if (current.messageType !== next.messageType) return false;
  if (
    current.messageType === MESSAGE_TYPES.TEMPLATE &&
    next.messageType === MESSAGE_TYPES.TEMPLATE
  )
    return false;
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

const ACTIVITY_DEDUP_WINDOW_SECONDS = 10 * 60;
const FAILED_DEDUP_WINDOW_SECONDS = 2 * 60;

const attachmentSignature = msg =>
  (msg.attachments || [])
    .map(attachment => attachment.fileType || attachment.contentType || 'file')
    .join(',');

const isActivityMessage = msg => msg.messageType === MESSAGE_TYPES.ACTIVITY;

const isFailedMessage = msg => msg.status === 'failed';

const isWameStickerMessage = message => {
  const attributes = message?.contentAttributes || {};
  const mediaType =
    attributes.wameMediaType ?? attributes.wame_media_type ?? '';

  return String(mediaType).toLowerCase() === 'sticker';
};

// Aceleres: mesma mecânica do álbum de fotos, mas pra stickers — grade fixa
// de 2 colunas estilo WhatsApp, sem legenda pra checar (sticker não tem).
//
// A figurinha VETORIAL (Lottie) entra aqui junto com as de imagem: o álbum
// desenha cada tile pelo tipo do anexo — <img> para a de imagem, canvas para
// a animação (ver StickerAlbum.vue). Foi por isso que ela ficou de fora
// durante um dia: o álbum só sabia <img>, e um JSON ali dava imagem quebrada.
const isStickerAlbumEligibleMessage = msg =>
  isGroupableMediaMessage(msg) && isWameStickerMessage(msg);

const isPairedWamePlaceholder = (message, messages) => {
  const attributes = message?.contentAttributes || {};
  const isWameSync =
    attributes.wameSync === true || attributes.wame_sync === true;
  const mediaType =
    attributes.wameMediaType ?? attributes.wame_media_type ?? '';
  const content = normalizeText(message?.content);

  if (
    !isWameSync ||
    String(mediaType).toLowerCase() !== 'text' ||
    !['[mensagem recebida]', '[message received]'].includes(content)
  ) {
    return false;
  }

  const createdAt = messageCreatedAt(message);
  return messages.some(candidate => {
    if (candidate.id === message.id || !isWameStickerMessage(candidate))
      return false;
    if (candidate.messageType !== message.messageType) return false;
    if (senderIdOf(candidate) !== senderIdOf(message)) return false;

    return Math.abs(messageCreatedAt(candidate) - createdAt) <= 5;
  });
};

const dedupWindowForMessage = msg =>
  isActivityMessage(msg)
    ? ACTIVITY_DEDUP_WINDOW_SECONDS
    : FAILED_DEDUP_WINDOW_SECONDS;

const dedupKeyForMessage = msg => {
  if (isActivityMessage(msg)) {
    const content = normalizeText(msg.content);
    return content ? `activity:${content}` : null;
  }

  if (isFailedMessage(msg)) {
    const content = normalizeText(msg.content);
    const attachments = attachmentSignature(msg);
    if (!content && !attachments) return null;

    return [
      'failed',
      msg.messageType,
      senderIdOf(msg) || '',
      content,
      attachments,
    ].join(':');
  }

  return null;
};

// Aceleres: a atividade e comparada so com a ATIVIDADE ANTERIOR, nunca com
// qualquer uma dos ultimos 10 minutos.
//
// Esta deduplicacao nasceu para conter tempestades de atividade identica: o
// laco em que o Laravel reimpunha o responsavel a cada evento, cada
// reimposicao gerando uma pilula nova. Conversa #10 em 20/08/2026: 17x o
// mesmo "Atribuido por Zoraxy para Angelo vendas", uma a cada ~10 min,
// intercaladas com a nota do rodizio. A causa foi fechada no backend na
// tarefa #2582 (ChatwootWebhookController).
//
// Comparar contra QUALQUER atividade da janela cobrava um preco que so
// aparecia numa frase. 'self_assigned' ("Fulano atribuiu a si mesmo essa
// conversa") e a unica atribuicao que NAO nomeia o destinatario, entao duas
// auto-atribuicoes do mesmo gestor produzem texto identico -- e o texto e a
// chave de deduplicacao. Medido em 24/08/2026 na conversa #10: 16:01:46 e
// 16:05:52, com duas transferencias e tres mensagens entre elas; a segunda
// sumia da tela mesmo sendo movimentacao real. Transferir para pessoas
// diferentes nunca colidia porque o nome do destinatario muda o texto.
//
// Olhando so para a anterior, a tempestade continua a colapsar (as identicas
// sao consecutivas ENTRE ATIVIDADES -- a nota do rodizio e message_type 1,
// nao entra nesta conta) e a movimentacao de verdade reaparece, porque entre
// duas auto-atribuicoes ha sempre outra atribuicao, com outro texto.
//
// `lastActivity` avanca tambem quando a atividade e descartada: sem isso um
// run de N identicas colapsaria so aos pares, e a terceira -- comparada com a
// primeira, ja fora da janela -- voltaria a aparecer.
const deduplicateRenderableMessages = messages => {
  const seenAtByKey = new Map();
  let lastActivity = null;

  return messages.filter(message => {
    if (isPairedWamePlaceholder(message, messages)) return false;

    const key = dedupKeyForMessage(message);
    if (!key) return true;

    const createdAt = messageCreatedAt(message);
    const dedupWindow = dedupWindowForMessage(message);

    if (isActivityMessage(message)) {
      if (
        lastActivity !== null &&
        lastActivity.key === key &&
        Math.abs(createdAt - lastActivity.createdAt) <= dedupWindow
      ) {
        lastActivity.createdAt = createdAt;
        return false;
      }

      lastActivity = { key, createdAt };
      return true;
    }

    const previousAt = seenAtByKey.get(key);

    if (
      previousAt !== undefined &&
      Math.abs(createdAt - previousAt) <= dedupWindow
    ) {
      return false;
    }

    seenAtByKey.set(key, createdAt);
    return true;
  });
};

// Aceleres: monta o maior run consecutivo (mesmo sender, mesmo messageType)
// a partir de `startIndex` cujas mensagens batem em `isEligible`. Usado tanto
// pro álbum de fotos/vídeos quanto pro de stickers — só o critério muda.
const buildEligibleRun = (messages, startIndex, isEligible) => {
  const run = [messages[startIndex]];
  let j = startIndex + 1;
  while (j < messages.length) {
    const next = messages[j];
    if (!isEligible(next)) break;
    const prev = run[run.length - 1];
    if (senderIdOf(next) !== senderIdOf(prev)) break;
    if (next.messageType !== prev.messageType) break;
    run.push(next);
    j += 1;
  }
  return { run, nextIndex: j };
};

// Aceleres: um grupo só "fecha" em álbum se TODAS as suas mensagens já lá
// estavam quando a conversa foi aberta (ver albumBaselineMessageId). A
// verificação acontece depois de fatiar o run para manter pares de stickers já
// consolidados quando chegam stickers novos.
//
// De quebra, isto tira o relógio reativo da conta: o cálculo deixou de ler
// qualquer fonte que mude com o tempo, então a lista já não é recalculada de
// 2 em 2 segundos.
const isRunSettled = run =>
  albumBaselineMessageId.value > 0 &&
  run.every(
    message => (Number(message.id) || 0) <= albumBaselineMessageId.value
  );

// Aceleres: os dois tipos de álbum usam a MESMA mecânica de agrupamento —
// monta o run elegível, espera settled (15s) — mas o que cada um FAZ com o
// run depois de fechado é diferente:
//   - 'album' (foto/vídeo): o run inteiro vira UMA bolha (grade só, com
//     resumo "+N" se passar de 4 azulejos — ver ImageAlbumBubble; o mínimo
//     para formar álbum é 3, mas o mosaico mostra até 4). chunkSize
//     Infinity = nunca fatia.
//   - 'sticker-album': cada PAR consecutivo vira sua PRÓPRIA bolha
//     separada — 6 stickers seguidos saem como 3 bolhas de 2, empilhadas
//     com o espaçamento normal entre mensagens, não uma grade só com 3
//     linhas. chunkSize 2 fatia o run nesse tamanho.
// Um resto que não fecha par (ex: run de 5 → par+par+1) fica de fora do
// álbum e renderiza como mensagem individual normal.
const ALBUM_KINDS = [
  {
    type: 'album',
    isEligible: isAlbumEligibleMessage,
    minSize: MIN_ALBUM_SIZE,
    chunkSize: Infinity,
  },
  {
    type: 'sticker-album',
    isEligible: isStickerAlbumEligibleMessage,
    minSize: MIN_STICKER_ALBUM_SIZE,
    chunkSize: MIN_STICKER_ALBUM_SIZE,
  },
];

const chunkRun = (run, size) => {
  if (!Number.isFinite(size)) return [run];

  const chunks = [];
  for (let offset = 0; offset < run.length; offset += size) {
    chunks.push(run.slice(offset, offset + size));
  }
  return chunks;
};

const renderedItems = computed(() => {
  const messages = deduplicateRenderableMessages(allMessages.value);
  // O limiar NÃO é calculado aqui de propósito: lê-lo antecipadamente ligava
  // esta computed ao relógio de 2s mesmo quando não havia álbum nenhum para
  // assentar. Agora quem lê é o isRunSettled, e só enquanto precisa.
  const items = [];
  let i = 0;
  while (i < messages.length) {
    const head = messages[i];
    const kind = ALBUM_KINDS.find(candidate => candidate.isEligible(head));
    let consumedAlbum = false;

    if (kind) {
      const { run, nextIndex } = buildEligibleRun(messages, i, kind.isEligible);
      if (run.length >= kind.minSize) {
        chunkRun(run, kind.chunkSize).forEach(group => {
          if (group.length >= kind.minSize && isRunSettled(group)) {
            items.push({
              type: kind.type,
              key: `${kind.type}-${group[0].id}`,
              messages: group,
              firstMessage: group[0],
              lastMessage: group[group.length - 1],
            });
          } else {
            // Aceleres: resto que não fechou par — cada mensagem volta a
            // ser um item normal, igual ao que já acontecia antes de
            // existir álbum.
            group.forEach(message => {
              items.push({
                type: 'message',
                key: message.id,
                message,
                firstMessage: message,
                lastMessage: message,
              });
            });
          }
        });
        i = nextIndex;
        consumedAlbum = true;
      }
    }

    if (!consumedAlbum) {
      items.push({
        type: 'message',
        key: head.id,
        message: head,
        firstMessage: head,
        lastMessage: head,
      });
      i += 1;
    }
  }
  return items;
});

onMounted(() => {
  emitter.on(
    BUS_EVENTS.MESSAGE_INTERACTION_OPENED,
    handleMessageInteractionOpened
  );
});

onBeforeUnmount(() => {
  removeConversationContextMenuListeners();
  emitter.off(
    BUS_EVENTS.MESSAGE_INTERACTION_OPENED,
    handleMessageInteractionOpened
  );
});
</script>

<template>
  <ul
    ref="messageListElement"
    v-bind="$attrs"
    class="acl-message-list__items pl-16 pr-12"
    @contextmenu.capture="openConversationContextMenu"
  >
    <slot name="beforeAll" />
    <template v-for="(item, index) in renderedItems" :key="item.key">
      <li
        v-if="shouldShowHistoryContext(item, renderedItems[index - 1])"
        class="acl-history-context flex items-center justify-center px-4 py-2 list-none"
      >
        <div
          class="acl-history-context__card max-w-[85%] rounded-[10px] px-3 py-2 text-center shadow-sm"
        >
          <div class="text-[12px] font-semibold leading-4">
            {{ historyPreviousLabel }}
          </div>
          <div class="mt-1 text-[12px] leading-4">
            {{ formatHistoryContext(getHistoryMetadata(item)) }}
          </div>
        </div>
      </li>
      <li
        v-if="
          index === 0 ||
          !isSameDay(
            renderedItems[index - 1].lastMessage.createdAt,
            item.firstMessage.createdAt
          )
        "
        class="acl-day-separator flex items-center justify-center gap-3 px-4 py-3 list-none"
      >
        <span
          class="acl-day-separator__label text-[12px] font-medium whitespace-nowrap rounded-[6px] py-[4px] px-[12px]"
        >
          {{ getDayLabel(item.firstMessage.createdAt) }}
        </span>
      </li>
      <slot
        v-if="
          firstUnreadId &&
          (item.type === 'message'
            ? item.message.id === firstUnreadId
            : item.messages.some(m => m.id === firstUnreadId))
        "
        name="unreadBadge"
      />
      <Message
        v-if="item.type === 'message'"
        v-bind="item.message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(item.message)"
        :group-with-next="
          shouldGroupWithNextMessage(
            item.message,
            renderedItems[index + 1]?.firstMessage
          )
        "
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', item.message)"
      />
      <Message
        v-else
        v-bind="item.lastMessage"
        :album-messages="item.messages"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="null"
        :group-with-next="false"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', item.lastMessage)"
      />
    </template>
    <slot name="after" />
  </ul>
  <Teleport v-if="conversationContextMenu.isOpen" to="body">
    <div
      ref="conversationContextMenuRef"
      class="acl-conversation-list-context-menu"
      :style="{
        left: conversationContextMenu.left,
        top: conversationContextMenu.top,
      }"
      @click.stop
      @contextmenu.prevent.stop
    >
      <button
        type="button"
        class="acl-conversation-list-context-menu__item flex-nowrap whitespace-nowrap"
        @click="startMessageSelection"
      >
        <span class="i-lucide-check-square size-4 shrink-0" />
        <span class="whitespace-nowrap">{{ conversationContextMenuLabels.selectMessages }}</span>
      </button>
      <button
        v-if="!isPassiveSupervisorView"
        type="button"
        class="acl-conversation-list-context-menu__item flex-nowrap whitespace-nowrap"
        @click="updateConversationReadState"
      >
        <span
          :class="
            hasUnreadMessages ? 'i-lucide-mail-open' : 'i-lucide-mail'
          "
          class="size-4 shrink-0"
        />
        <span class="whitespace-nowrap">
          {{
            hasUnreadMessages
              ? conversationContextMenuLabels.markAsRead
              : conversationContextMenuLabels.markAsUnread
          }}
        </span>
      </button>
      <button
        type="button"
        class="acl-conversation-list-context-menu__item flex-nowrap whitespace-nowrap"
        @click="toggleConversationPin"
      >
        <span
          :class="
            isConversationPinned ? 'i-lucide-pin-off' : 'i-lucide-pin'
          "
          class="size-4 shrink-0"
        />
        <span class="whitespace-nowrap">
          {{
            isConversationPinned
              ? conversationContextMenuLabels.unpinConversation
              : conversationContextMenuLabels.pinConversation
          }}
        </span>
      </button>
      <button
        type="button"
        class="acl-conversation-list-context-menu__item flex-nowrap whitespace-nowrap"
        :disabled="isConversationResolved"
        @click="closeConversation"
      >
        <span class="i-lucide-circle-x size-4 shrink-0" />
        <span class="whitespace-nowrap">{{ conversationContextMenuLabels.closeConversation }}</span>
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
.acl-history-context__card {
  color: rgba(0, 0, 0, 0.72);
  background-color: var(
    --WAWeb-minimal-system-bubble-bg,
    var(--WDS-systems-bubble-surface-system, rgba(255, 255, 255, 0.9))
  );
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.acl-day-separator__label {
  color: rgba(0, 0, 0, 0.6);
  background-color: var(
    --WAWeb-minimal-system-bubble-bg,
    var(--WDS-systems-bubble-surface-system, rgba(255, 255, 255, 0.9))
  );
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Aceleres 2026-08-17: acima do compositor. O shell do compositor
   (MessagesView.vue) e um contexto de empilhamento com z-index 2147482000 e
   `isolation: isolate`, entao qualquer menu na casa dos milhares — mesmo
   teleportado para o body — era pintado ATRAS dele e ficava escondido ou
   inclicavel na parte de baixo da conversa.

   2147482700 nao e um numero novo: e a mesma faixa que os menus flutuantes do
   mobile ja usam (aceleres-overrides.css), acima do compositor e abaixo do
   aviso de "digitando" (2147483000) e dos modais (2147483500+). */
.acl-conversation-list-context-menu {
  position: fixed;
  z-index: 2147482700;
  /* Pedido 2026-09-02: a largura passa a ser a do conteudo. Os 240px fixos
     sobravam — o item mais largo e "Selecionar mensagens", com icone, e nao
     chega la. `max-content` porque os itens ja sao `whitespace-nowrap`: sem
     isso o menu manteria a caixa larga com o texto colado a esquerda.

     Os 240 do clamp de posicao (ver `layoutToVisual(240, ...)` no script)
     ficam de proposito: com a largura real menor, usar 240 na conta so afasta
     o menu da borda um pouco mais do que o necessario. Se algum dia um rotulo
     passar dos 240, e la que a conta tem de mudar, senao o menu sai do ecra. */
  width: max-content;
  max-width: calc(100vw - 16px);
  padding: 8px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    0 14px 32px rgba(15, 23, 42, 0.18),
    0 3px 8px rgba(15, 23, 42, 0.08);
}

.acl-conversation-list-context-menu__item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 2.4rem;
  gap: 8px;
  padding: 0 10px;
  border: 0;
  border-radius: 10px;
  color: #111827;
  font-size: 14px;
  line-height: 1;
  background: transparent;
  cursor: pointer;
}

.acl-conversation-list-context-menu__item:hover:not(:disabled) {
  background: rgba(15, 23, 42, 0.06);
}

.acl-conversation-list-context-menu__item:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.dark .acl-conversation-list-context-menu {
  background: rgba(24, 24, 27, 0.98);
  border-color: rgba(255, 255, 255, 0.08);
}

.dark .acl-conversation-list-context-menu__item {
  color: #f8fafc;
}

.dark .acl-conversation-list-context-menu__item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}
</style>
